import {
  blockAsParsed,
  charactersDefaults,
  DEFAULT_RESURRECTION_BANDS,
  resurrectionDefaults,
  resurrectionKitDefaults,
} from '../teamsDefaults';
import type { Err } from './common';
import {
  CHARACTER_DELETE_CONFIRMS,
  MAX_CHARACTER_SLOTS,
  type Bundle,
  type ResurrectionBandDef,
  type StoreProductDef,
} from './types';

/*
 * Characters and paid resurrection (ADR 0069; `characters`, `resurrection` and the `resurrection` store products): a port of
 * packages/shared-cs/Runtime/Content/ContentValidator.Characters.cs with the same messages in the same order. The server runs it right
 * after the Store check, so validateTeams calls it there. An absent section is its C# defaults; an explicit `null` section is skipped
 * (the C# has no "missing" error for these). `validate.characters.test.ts` pins every message against a C# dump.
 */

const MAX_KIT_QUANTITY = 999;
const n = (v: number | null | undefined) => v ?? 0;

export function validateCharacters(b: Bundle, err: Err) {
  const c = blockAsParsed(charactersDefaults, b.characters);
  if (c) {
    const w = 'Characters';
    const e = (path: string, m: string) => err('characters', undefined, path, m);
    const vip = n(c.vipSlots);
    if (vip < 1 || vip > MAX_CHARACTER_SLOTS)
      e('vipSlots', `${w} need vipSlots in 1..${MAX_CHARACTER_SLOTS}.`);
    if (n(c.maxSlots) < 1 || n(c.maxSlots) > vip)
      e('maxSlots', `${w} need maxSlots in 1..vipSlots.`);
    if (!(CHARACTER_DELETE_CONFIRMS as readonly string[]).includes(c.deleteConfirm ?? ''))
      e('deleteConfirm', `${w} has unknown deleteConfirm '${c.deleteConfirm ?? ''}'.`);
    // friendshipsAllCharacters is a plain bool: the C# validator has no rule for it, so neither does this mirror.
  }

  const r = blockAsParsed(resurrectionDefaults, b.resurrection);
  if (!r) return;
  const rw = 'Resurrection';
  const re = (path: string, m: string) => err('resurrection', undefined, path, m);
  const own = r.bands != null && r.bands.length > 0;
  const bands: (ResurrectionBandDef | null)[] = own
    ? r.bands!.map((x) => (x == null ? null : { minDays: 0, tier: 0, ...x }))
    : DEFAULT_RESURRECTION_BANDS.map((x) => ({ ...x }));
  bands.forEach((band, i) => {
    const at = own ? `bands.${i}` : 'bands';
    if (band == null) {
      re(at, `${rw} has an empty band.`);
      return;
    }
    const prev = bands[i - 1];
    if (i === 0 && n(band.minDays) !== 0)
      re(`${at}.minDays`, `${rw} bands must start at minDays 0.`);
    if (i > 0 && prev != null && n(band.minDays) <= n(prev.minDays))
      re(`${at}.minDays`, `${rw} bands must be in ascending minDays order.`);
    if (n(band.tier) !== i + 1)
      re(`${at}.tier`, `${rw} band ${i + 1} needs tier ${i + 1} (tiers 1..N in order).`);
  });

  if (r.kit === null) re('kit', `${rw} is missing 'kit'.`);
  else {
    const k = blockAsParsed(resurrectionKitDefaults, r.kit)!;
    for (const f of ['foodDays', 'waterDays', 'magazinesForBestGun', 'bandages'] as const) {
      const v = n(k[f]);
      if (v < 0 || v > MAX_KIT_QUANTITY)
        re(`kit.${f}`, `${rw} kit needs ${f} in 0..${MAX_KIT_QUANTITY}.`);
    }
  }

  if (b.store === null) return;
  const products = (b.store?.products ?? []) as (StoreProductDef | null)[] | null;
  if (products == null) return;
  const tiers = bands.length;
  const perTier = new Array<number>(tiers + 1).fill(0);
  let any = false;
  products.forEach((p, j) => {
    if (p == null) return;
    const pw = `Store product '${p.id ?? ''}'`;
    const pe = (m: string) => err('store', undefined, `products.${j}.tier`, m);
    const tier = p.tier ?? null;
    if (p.kind !== 'resurrection') {
      if (tier != null) pe(`${pw} has a tier but is not a resurrection product.`);
      return;
    }
    any = true;
    if (tier == null || tier < 1 || tier > tiers) pe(`${pw} needs a tier in 1..${tiers}.`);
    else perTier[tier]!++;
  });
  if (!any) return;
  for (let t = 1; t <= tiers; t++)
    if (perTier[t] !== 1)
      err(
        'store',
        undefined,
        'products',
        `Store needs exactly one resurrection product for tier ${t} (has ${perTier[t]}).`,
      );
}
